import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { PageLoaderComponent } from '@shared/components/page-loader/page-loader.component';
import { Subscription } from 'rxjs';

interface ILoginState {
  afterPasswordForgot?: boolean;
  afterPasswordReset?: boolean;
}

@Component({
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    TranslateModule,
    IconComponent,
    PageLoaderComponent,
  ],
  selector: 'app-login',
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit, OnDestroy {
  public readonly loginForm: FormGroup;

  public readonly showPassword = signal(false);
  public readonly isEmailStep = signal(true);
  public readonly isLoading = signal(false);
  public readonly afterPasswordForgot = signal(false);
  public readonly afterPasswordReset = signal(false);

  public returnUrl: string;

  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly subscriptions = new Subscription();

  constructor() {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.setStateFlags();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.authService.isAuthenticating$.subscribe((isAuthenticating) => {
        this.isLoading.set(isAuthenticating);
      }),
    );

    this.subscriptions.add(
      this.authService.isAuthenticated().subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          void this.router.navigate([this.returnUrl]);
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public async onContinue(): Promise<void> {
    if (this.loginForm.get('email')?.invalid) {
      return;
    }

    this.isEmailStep.set(false);
  }

  public async onGoogleLogin(): Promise<void> {
    try {
      await this.authService.signInWithGoogle();
    } catch {
      this.snackBarService.openError(
        this.translate.instant('pages.login.snackbar.accountNotLinkedWithGoogle'),
        this.translate.instant('shared.close'),
      );
    }
  }

  public async onFacebookLogin(): Promise<void> {
    try {
      await this.authService.signInWithFacebook();
    } catch {
      this.snackBarService.openError(
        this.translate.instant('pages.login.snackbar.accountNotLinkedWithFacebook'),
        this.translate.instant('shared.close'),
      );
    }
  }

  public async onTwitterLogin(): Promise<void> {
    try {
      await this.authService.signInWithTwitter();
    } catch {
      this.snackBarService.openError(
        this.translate.instant('pages.login.snackbar.accountNotLinkedWithTwitter'),
        this.translate.instant('shared.close'),
      );
    }
  }

  public async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    try {
      const { email, password } = this.loginForm.value;
      await this.authService.signInWithEmail(email, password);
    } catch {
      this.snackBarService.openError(
        this.translate.instant('pages.login.snackbar.invalidCredentials'),
        this.translate.instant('shared.close'),
      );
    }
  }

  public async onForgotPassword(): Promise<void> {
    void this.router.navigate(['/auth/forgot-password']);
  }

  public togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  private setStateFlags(): void {
    const state: ILoginState | undefined = this.router.getCurrentNavigation()?.extras.state;

    this.afterPasswordForgot.set(!!state?.afterPasswordForgot);
    this.afterPasswordReset.set(!!state?.afterPasswordReset);

    this.resetUrlState();
  }

  private resetUrlState(): void {
    const { afterPasswordForgot, afterPasswordReset } = window.history.state as ILoginState;
    const hasEphemeralFlags = !!(afterPasswordForgot || afterPasswordReset);

    if (hasEphemeralFlags) {
      window.history.replaceState({}, '', this.router.url);
    }
  }
}
